import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionData } from '../../../hooks/order';
import useGoogleSignIn from '../../../hooks/useGoogleSignIn';
import useAppleSignIn from '../../../hooks/useAppleSignIn';
import useWindowDimensions from '../../common/useWindowDimensions';
import CompatibilityCheck from '../Checkout/CompatibilityCheck';
import EnterInformation from '../Checkout/EnterInformation';
import CreateAccount from '../Checkout/CreateAccount';
import { GoogleIcon } from '../../../images/svg';

const YourInformationSection = ({
  activeKey,
  setActiveKey,
  isMobileAdded,
  showCreateAccTile,
  childRef,
  setShowCallSupportModal,
}) => {
  const { t } = useTranslation();
  const { orderInfo } = useSessionData();
  const { isMobile } = useWindowDimensions();
  const [ssoPrefill, setSsoPrefill] = useState(null);
  // 'google' | 'apple' | null - which provider's inline error to show.
  const [ssoError, setSsoError] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);
  // The provider the customer last clicked. An error is only shown for that provider, so one
  // reported without a click (e.g. Google's script blocked at mount) stays silent.
  const ssoAttemptRef = useRef(null);

  const handleSsoSuccess = (profile) => {
    setSsoError(null);
    // Only the fields the provider returned - Apple sends the name only the first time a
    // customer approves EarthLink, and an empty value would wipe a name already in the form.
    const prefill = Object.fromEntries(Object.entries(profile).filter(([, value]) => value));
    if (Object.keys(prefill).length) {setSsoPrefill(prefill);}
    setShowManualForm(true);
  };
  const handleSsoError = (provider) => () => {
    if (ssoAttemptRef.current === provider) {setSsoError(provider);}
  };
  // trigger() stays synchronous inside the click, so the provider's popup isn't blocked.
  const startSsoAttempt = (provider, trigger) => () => {
    ssoAttemptRef.current = provider;
    setSsoError(null);
    trigger();
  };

  const { isConfigured, triggerSignIn } = useGoogleSignIn({
    onSuccess: handleSsoSuccess,
    onError: handleSsoError('google'),
  });

  const { isConfigured: isAppleConfigured, triggerSignIn: triggerAppleSignIn } = useAppleSignIn({
    onSuccess: handleSsoSuccess,
    onError: handleSsoError('apple'),
  });

  const compatibilityActive = isMobileAdded && activeKey === '2';
  const compatibilityDone = isMobileAdded && parseInt(activeKey, 10) > 2;
  const infoActive = activeKey === '3';
  const createAccountActive = showCreateAccTile && activeKey === '4';

  // The Google/Apple profile only fills the form for the visit it was fetched in.
  // EnterInformation re-mounts every time this step reopens and restores from orderInfo -
  // keeping the prefill around would overwrite whatever the customer edited and saved with the
  // provider's values again.
  useEffect(() => {
    if (!infoActive) {setSsoPrefill(null);}
  }, [infoActive]);

  const sectionActive = compatibilityActive || infoActive || createAccountActive;
  const lastStepKey = showCreateAccTile ? 4 : 3;
  const sectionDone = parseInt(activeKey, 10) > lastStepKey;
  const isInteractive = Boolean(activeKey);

  const shouldShowManualToggle = isMobile && !(showManualForm || ssoPrefill);

  return (
    <div className={`spc-card spc-information-section ${sectionActive ? 'is-expanded' : 'is-collapsed'}`}>
      <div
        className={`spc-card__collapsed-row ${isInteractive ? 'spc-card__collapsed-row--clickable' : ''}`}
        onClick={() => isInteractive && setActiveKey(isMobileAdded ? '2' : '3')}
      >
        <div className="spc-collapsed-plan spc-collapsed-info">
          <span className="spc-collapsed-plan__title">
            {t('checkout.yourInformation')}{sectionDone ? ' -' : ''}
          </span>
          {sectionDone && (
            <span className="spc-collapsed-plan__summary">
              {[
                [orderInfo?.firstName, orderInfo?.lastName].filter(Boolean).join(' '),
                orderInfo?.email,
                orderInfo?.phoneNum?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3'),
              ].filter(Boolean).join(', ')}
            </span>
          )}
        </div>
        {sectionDone && (
          <button
            type="button"
            className="spc-link-button spc-change-link"
            onClick={(e) => {
              e.stopPropagation();
              setActiveKey(isMobileAdded ? '2' : '3');
            }}
          >
            {t('common.change')}
          </button>
        )}
      </div>

      {isMobileAdded && (compatibilityActive || compatibilityDone) && (
        <div className={compatibilityActive ? 'spc-subsection is-active' : 'spc-subsection is-done'}>
          <h4 className="spc-subsection__title">{t('checkout.deviceCompatibility')}</h4>
          {compatibilityActive && <CompatibilityCheck setActiveKey={setActiveKey} activeKey={activeKey} />}
        </div>
      )}

      {infoActive && (
        <div className="spc-subsection is-active">
          <p className="spc-subsection__subtitle">{t('singlePageCheckout.yourInfoSubtitle')}</p>

          <div className="spc-sso-buttons spc-sso-buttons--row">
            <button
              type="button"
              className="spc-sso-button spc-sso-button--google"
              disabled={!isConfigured}
              onClick={startSsoAttempt('google', triggerSignIn)}
            >
              <GoogleIcon />
              {t('singlePageCheckout.continueWithGoogle')}
            </button>
            {isAppleConfigured && (
              <button
                type="button"
                className="spc-sso-button spc-sso-button--apple"
                onClick={startSsoAttempt('apple', triggerAppleSignIn)}
              >
                <i className="fa-brands fa-apple"></i>
                {t('singlePageCheckout.continueWithApple')}
              </button>
            )}
          </div>
          {ssoError === 'google' && <p className="spc-google-error" role="alert">{t('singlePageCheckout.googleSignInError')}</p>}
          {ssoError === 'apple' && <p className="spc-google-error" role="alert">{t('singlePageCheckout.appleSignInError')}</p>}

          <div className="spc-or-divider"><span>{t('singlePageCheckout.or')}</span></div>

          {shouldShowManualToggle ? (
            <div className="spc-enter-manual-container">
              <button
                type="button"
                className="spc-enter-manual-button"
                onClick={() => setShowManualForm(true)}
              >
                {t('singlePageCheckout.enterAccountManually')}
              </button>
            </div>
          ) : (
            <EnterInformation
              ref={childRef}
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              setShowCallSupportModal={setShowCallSupportModal}
              showCreateAccTile={showCreateAccTile}
              prefillValues={ssoPrefill}
              showFieldLabels
            />
          )}
        </div>
      )}

      {showCreateAccTile && createAccountActive && (
        <div className="spc-subsection is-active">
          <h4 className="spc-subsection__title">{t('checkout.createAccount')}</h4>
          <CreateAccount
            ref={childRef}
            disableWhenIncomplete
            activeKey={activeKey}
            setActiveKey={setActiveKey}
            setShowCallSupportModal={setShowCallSupportModal}
          />
        </div>
      )}
    </div>
  );
};

export default YourInformationSection;
